import type {
  Field,
  Swimlane,
  Step,
  Rule,
  BulkAction,
  ProcessProps
} from "@certa/studio-core";
import type {
  ConditionData,
  ConditionGroupData
} from "../components/RulesUI/Constraints/constraints.types";

/**
 * Decorator function that take a function as input and returns a function that
 * inserts the __createdAt and __modifiedAt properties in the last argument and calls the original function
 * with the updated arguments
 *
 * Also inserts support to pass an optional argument in the returned function to skip updating the __modifiedAt property
 *
 * Why are we changing last argument?
 * Because in setter functions,
 * the last argument is the entity value that is going to be created or updated by the setter function
 * The signature of the setter function is: studioState().setField(fieldId: string, field: Field) -or- studioState().addField(field: Field)
 */
const insertTimeStampsInSetter = <
  T extends (...args: any[]) => ReturnType<T>,
  S extends Field | Step | Swimlane | Rule | ConditionData | ConditionGroupData
>(
  setterFn: T,
  isUpdaterFn: boolean = false
) => {
  return (
    ...args: [...Parameters<T>, shouldSkipUpdatingModifiedAt?: boolean]
  ) => {
    // Flag to indicate whether to skip updating modifiedAt timestamp, by default it is false
    let shouldSkipUpdatingModifiedAt = false;

    // Check if the last argument is a boolean (flag is passed from caller fn), and update flag accordingly
    const lastArg = args.at(-1) as S | boolean;
    if (typeof lastArg === "boolean") {
      shouldSkipUpdatingModifiedAt = lastArg;
      args.pop(); // Remove the boolean argument from args
    }

    // Entity is always be the last argument after removing optional flag, see studioState().updateField/setField/addField syntax
    const entity = { ...args.at(-1) } as S;

    // Skipping updating the __modifiedAt property if the flag is true
    if (!shouldSkipUpdatingModifiedAt) {
      entity.__modifiedAt = Date.now();
    }

    // For updaters, we don't need to add __createdAt property
    // as partial updates are allowed and not always createdAt is passed
    // For setters, If it is a new entity, adding __createdAt property
    if (!isUpdaterFn) {
      entity.__createdAt = entity.__createdAt || Date.now();
    }

    // Call the original function with the modified arguments
    const newArgs = [...args.slice(0, args.length - 1), entity];
    return setterFn(...newArgs);
  };
};

/**
 * Same as insertTimeStampsInSetter but for bulk setter functions,
 * here we insert the __createdAt and __modifiedAt properties in each entity of the last argument array and call the original function
 *
 * Also adds support to pass an optional argument in the returned function to skip updating the __modifiedAt property
 *
 * Why are we changing last argument?
 * Because in bulk setter functions,
 * the last argument is an array of entity values that are going to be created or updated by the bulk setter function
 * The signature of the setter function is: studioState().bulkSet(fields: Field[])
 */
const insertTimeStampsInBulkSetter = <
  T extends (...args: any[]) => ReturnType<T>,
  S extends Field | Step | Swimlane | Rule | ConditionData | ConditionGroupData
>(
  bulkSetterFn: T
) => {
  return (
    ...args: [...Parameters<T>, shouldSkipUpdatingModifiedAt?: boolean]
  ) => {
    // Flag to indicate whether to skip updating the modifiedAt timestamp, by default it is false
    let shouldSkipUpdatingModifiedAt = false;

    // Check if the last argument is a boolean (flag is passed from caller fn), and update flag accordingly
    const lastArg = args.at(-1) as S[] | boolean;
    if (typeof lastArg === "boolean") {
      shouldSkipUpdatingModifiedAt = lastArg;
      args.pop(); // Remove the boolean argument from args
    }

    // Entity is always be the last argument now, see studioState().bulkSet syntax
    let entities = args.at(-1) as S[];

    entities = entities.map(entity => {
      if (!shouldSkipUpdatingModifiedAt) {
        // skip updating the __modifiedAt property if the flag is true
        entity.__modifiedAt = Date.now();
      }

      // If it is a new entity, adding __createdAt property
      entity.__createdAt = entity.__createdAt || Date.now();

      return entity;
    });

    // Call the original function with the modified arguments
    const newArgs = [...args.slice(0, args.length - 1), entities];
    return bulkSetterFn(...newArgs);
  };
};

function insertTimeStampInValue<
  T extends
    | Field
    | Step
    | Swimlane
    | Rule
    | ConditionData
    | ConditionGroupData
    | BulkAction
    | ProcessProps
>(value: T): T {
  const now = Date.now();

  // updating the __modifiedAt property
  value.__modifiedAt = now;

  // If it is a new entity, adding __createdAt property
  value.__createdAt = value.__createdAt || now;

  return value;
}

export {
  insertTimeStampsInSetter,
  insertTimeStampsInBulkSetter,
  insertTimeStampInValue
};
